<template>
  <PageLayout title="Dashboard" subtitle="Mission overview">
    <template v-if="isDirector" #actions>
      <router-link to="/devices/pair" class="btn btn-outline-primary btn-sm">
        <i class="fas fa-link mr-1"></i> Pair Device
      </router-link>
      <button class="btn btn-primary btn-sm" @click="onCreateWorkspace">
        <i class="fas fa-plus mr-1"></i> New Workspace
      </button>
    </template>

    <div class="dash-hero mb-30">
      <div class="dash-hero__main">
        <p class="dash-hero__kicker">ControlLabs</p>
        <h2 class="dash-hero__title">{{ greeting }}, {{ userName }}</h2>
        <p class="dash-hero__meta">
          <span class="dash-chip">{{ user?.level || 'user' }}</span>
          <span v-if="isDirector" class="dash-hero__summary">
            {{ workspaces.length }} workspace · {{ onlineCount }}/{{ nodes.length }} device online
          </span>
        </p>
      </div>
      <div class="dash-hero__aside">
        <span class="dash-live" :class="wsConnected ? 'is-on' : 'is-off'">
          <i class="fas fa-circle"></i>
          {{ wsConnected ? 'Live' : 'Offline' }}
        </span>
        <div class="dash-hero__clock">{{ clock }}</div>
        <div class="dash-hero__date">{{ today }}</div>
      </div>
    </div>

    <div v-if="!isDirector" class="alert alert-warning mb-30">
      Akun Anda berlevel <strong>{{ user?.level || 'user' }}</strong>.
      Menu Workspace, Devices, dan Macros hanya untuk <strong>director</strong>.
    </div>

    <div v-if="isDirector" class="row">
      <div v-for="card in statCards" :key="card.title" class="col-6 col-xl-3 mb-30">
        <div class="dashboard-stat-card" :class="`dashboard-stat-card--${card.tone}`">
          <div class="dashboard-stat-card__icon"><i :class="card.icon"></i></div>
          <div class="dashboard-stat-card__body">
            <div class="dashboard-stat-card__value">{{ card.value }}</div>
            <div class="dashboard-stat-card__label">{{ card.title }}</div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="isDirector">
      <div class="row">
        <div class="col-lg-8">
          <ContentCard>
            <div class="dashboard-card-header d-flex justify-content-between align-items-start">
              <div>
                <h5 class="dashboard-card-title">Workspaces</h5>
                <span class="dashboard-card-subtitle">Buka layout produksi</span>
              </div>
              <button class="btn btn-sm btn-outline-primary" @click="onCreateWorkspace">
                <i class="fas fa-plus"></i>
              </button>
            </div>

            <div v-if="!workspaces.length" class="dash-empty">
              <i class="fas fa-th-large"></i>
              <p>Belum ada workspace.</p>
              <button class="btn btn-primary btn-sm" @click="onCreateWorkspace">Buat workspace</button>
            </div>
            <div v-else class="dash-ws-grid">
              <router-link
                v-for="ws in workspaces"
                :key="ws.id"
                :to="`/workspaces/${ws.id}`"
                class="dash-ws-card"
              >
                <span class="dash-ws-card__icon"><i class="fas fa-th-large"></i></span>
                <span class="dash-ws-card__body">
                  <strong>{{ ws.name }}</strong>
                  <small v-if="ws.is_default">Default</small>
                  <small v-else>Workspace</small>
                </span>
                <i class="fas fa-chevron-right dash-ws-card__go"></i>
              </router-link>
            </div>
          </ContentCard>

          <ContentCard>
            <div class="dashboard-card-header d-flex justify-content-between align-items-start">
              <div>
                <h5 class="dashboard-card-title">Device fleet</h5>
                <span class="dashboard-card-subtitle">Connector nodes</span>
              </div>
              <router-link to="/devices" class="btn btn-sm btn-outline-secondary">All devices</router-link>
            </div>

            <div v-if="nodes.length" class="dash-health mb-3">
              <div class="dash-health__track">
                <div class="dash-health__fill" :style="{ width: onlinePct + '%' }"></div>
              </div>
              <span class="dash-health__label">{{ onlinePct }}% online</span>
            </div>

            <div v-if="!nodes.length" class="dash-empty">
              <i class="fas fa-server"></i>
              <p>Belum ada device terpasang.</p>
              <router-link to="/devices/pair" class="btn btn-outline-primary btn-sm">Pair device</router-link>
            </div>
            <ul v-else class="dash-device-list">
              <li v-for="node in nodes" :key="node.id" class="dash-device">
                <span class="dash-device__dot" :class="node.online ? 'is-on' : 'is-off'"></span>
                <div class="dash-device__main">
                  <strong>{{ node.name }}</strong>
                  <div class="dash-device__meta">
                    <span v-if="cpuOf(node) != null">CPU {{ cpuOf(node) }}%</span>
                    <span v-for="d in (node.devices || []).slice(0, 3)" :key="d.id">{{ d.adapter }}</span>
                    <span v-if="(node.devices || []).length > 3">+{{ node.devices.length - 3 }}</span>
                  </div>
                </div>
                <span v-if="node.ready_status" class="badge badge-danger">GO</span>
                <span v-else class="badge" :class="node.online ? 'badge-success' : 'badge-secondary'">
                  {{ node.online ? 'Online' : 'Offline' }}
                </span>
              </li>
            </ul>
          </ContentCard>
        </div>

        <div class="col-lg-4">
          <ContentCard>
            <div class="dashboard-card-header">
              <h5 class="dashboard-card-title">Quick launch</h5>
              <span class="dashboard-card-subtitle">Aksi cepat</span>
            </div>
            <div class="dash-actions">
              <router-link
                v-if="workspaces.length"
                :to="`/workspaces/${workspaces[0].id}`"
                class="dash-action"
              >
                <i class="fas fa-rocket"></i>
                <span>Open workspace</span>
              </router-link>
              <router-link to="/devices/pair" class="dash-action">
                <i class="fas fa-link"></i>
                <span>Pair device</span>
              </router-link>
              <router-link to="/macros" class="dash-action">
                <i class="fas fa-play-circle"></i>
                <span>Run macros</span>
              </router-link>
            </div>
          </ContentCard>

          <ContentCard>
            <div class="dashboard-card-header d-flex justify-content-between align-items-start">
              <div>
                <h5 class="dashboard-card-title">Live activity</h5>
                <span class="dashboard-card-subtitle">Event terbaru</span>
              </div>
            </div>
            <ul v-if="recentEvents.length" class="dash-feed">
              <li v-for="(ev, i) in recentEvents" :key="i" class="dash-feed__item">
                <span class="dash-feed__time">{{ formatTime(ev.timestamp || ev.created_at) }}</span>
                <span class="dash-feed__body">
                  <strong>{{ nodeName(ev.nodeId || ev.node_id) }}</strong>
                  {{ ev.action || ev.type || 'event' }}
                  <em v-if="ev.adapter">{{ ev.adapter }}</em>
                </span>
              </li>
            </ul>
            <p v-else class="text-muted small mb-0">Menunggu event live…</p>
          </ContentCard>
        </div>
      </div>
    </template>

    <div v-else class="row">
      <div class="col-lg-6">
        <ContentCard>
          <div class="dashboard-card-header">
            <h5 class="dashboard-card-title">Akun</h5>
          </div>
          <div class="dashboard-account-list">
            <div class="dashboard-account-item">
              <span>Nama</span>
              <strong>{{ userName }}</strong>
            </div>
            <div class="dashboard-account-item">
              <span>Email</span>
              <strong>{{ user?.email || '—' }}</strong>
            </div>
            <div class="dashboard-account-item">
              <span>Role</span>
              <strong>{{ user?.level || '—' }}</strong>
            </div>
            <div class="dashboard-account-item">
              <span>Status</span>
              <strong>{{ user?.status === 1 ? 'Active' : 'Inactive' }}</strong>
            </div>
          </div>
        </ContentCard>
      </div>
    </div>

    <modal name="create-workspace" height="auto" :width="420" :adaptive="true">
      <div class="p-4">
        <h5 class="mb-3">New Workspace</h5>
        <div class="form-group">
          <label class="small text-muted">Name</label>
          <input
            v-model="createForm.name"
            class="form-control"
            maxlength="100"
            placeholder="Workspace name"
            @keyup.enter="saveCreateWorkspace"
          >
        </div>
        <div class="d-flex justify-content-end">
          <button class="btn btn-light mr-2" @click="$modal.hide('create-workspace')">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="!createForm.name.trim() || creating"
            @click="saveCreateWorkspace"
          >
            {{ creating ? 'Creating...' : 'Create' }}
          </button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  data() {
    return {
      now: new Date(),
      clockTimer: null,
      pollNodes: null,
      createForm: { name: '' },
      creating: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['user', 'role']),
    ...mapGetters('workspace', ['workspaces']),
    ...mapGetters('mission', ['nodes', 'wsConnected', 'liveEvents']),
    ...mapGetters('macro', ['macros']),
    isDirector() {
      return this.role === 'director' || this.role === 'admin'
    },
    userName() {
      return this.user?.name || 'User'
    },
    greeting() {
      const h = this.now.getHours()
      if (h < 5 || h >= 18) return 'Selamat malam'
      if (h < 11) return 'Selamat pagi'
      if (h < 15) return 'Selamat siang'
      return 'Selamat sore'
    },
    clock() {
      return this.now.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    today() {
      return this.now.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })
    },
    onlineCount() {
      return this.nodes.filter((n) => n.online).length
    },
    goCount() {
      return this.nodes.filter((n) => n.ready_status).length
    },
    onlinePct() {
      if (!this.nodes.length) return 0
      return Math.round((this.onlineCount / this.nodes.length) * 100)
    },
    statCards() {
      return [
        { title: 'Workspaces', value: this.workspaces.length, icon: 'fas fa-th-large', tone: 'primary' },
        { title: 'Devices online', value: `${this.onlineCount}/${this.nodes.length}`, icon: 'fas fa-server', tone: 'success' },
        { title: 'Macros', value: this.macros.length, icon: 'fas fa-play-circle', tone: 'info' },
        { title: 'GO ready', value: this.goCount, icon: 'fas fa-rocket', tone: this.goCount ? 'danger' : 'warning' },
      ]
    },
    recentEvents() {
      return (this.liveEvents || []).slice(0, 10)
    },
  },
  watch: {
    isDirector: {
      immediate: true,
      handler(val) {
        if (val) this.loadDirectorData()
      },
    },
  },
  mounted() {
    this.clockTimer = setInterval(() => { this.now = new Date() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.clockTimer)
    clearInterval(this.pollNodes)
    if (this.isDirector) this.disconnectWS()
  },
  methods: {
    ...mapActions('workspace', ['fetchWorkspaces', 'createWorkspace']),
    ...mapActions('mission', ['fetchNodes', 'connectWS', 'disconnectWS']),
    ...mapActions('macro', ['fetchMacros']),
    async loadDirectorData() {
      try {
        await Promise.all([this.fetchWorkspaces(), this.fetchNodes(), this.fetchMacros()])
      } catch (e) {
        // ignore — non-director / network
      }
      this.connectWS()
      clearInterval(this.pollNodes)
      this.pollNodes = setInterval(() => this.fetchNodes(), 10000)
    },
    cpuOf(node) {
      const raw = node && node.system_stats
      if (!raw) return null
      try {
        const s = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (s.cpuPercent == null) return null
        return Math.round(Number(s.cpuPercent))
      } catch (e) {
        return null
      }
    },
    nodeName(id) {
      if (!id) return 'Hub'
      const n = this.nodes.find((x) => x.id === id)
      return n ? n.name : String(id).slice(0, 8)
    },
    formatTime(t) {
      if (!t) return '--:--:--'
      const d = new Date(t)
      if (Number.isNaN(d.getTime())) return '--:--:--'
      return d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },
    onCreateWorkspace() {
      this.createForm = { name: '' }
      this.creating = false
      this.$modal.show('create-workspace')
    },
    async saveCreateWorkspace() {
      const name = this.createForm.name.trim()
      if (!name || this.creating) return
      this.creating = true
      try {
        const ws = await this.createWorkspace(name)
        this.$modal.hide('create-workspace')
        this.$router.push(`/workspaces/${ws.id}`)
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to create workspace')
      } finally {
        this.creating = false
      }
    },
  },
}
</script>

<style scoped>
.dash-hero {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.75rem 1.875rem;
  border-radius: var(--wc-radius-xl);
  background:
    radial-gradient(1200px 180px at 100% 0%, rgba(255, 255, 255, 0.14), transparent 50%),
    linear-gradient(135deg, var(--wc-primary) 0%, #0a5c54 100%);
  color: #fff;
  box-shadow: var(--wc-shadow);
}
.dash-hero__kicker {
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.75;
  font-weight: 600;
}
.dash-hero__title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 600;
  line-height: 1.25;
}
.dash-hero__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem;
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  opacity: 0.92;
}
.dash-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}
.dash-hero__aside {
  text-align: right;
}
.dash-hero__clock {
  font-size: 1.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  line-height: 1.1;
  margin-top: 0.5rem;
}
.dash-hero__date {
  font-size: 0.8125rem;
  opacity: 0.8;
  text-transform: capitalize;
  margin-top: 0.25rem;
}
.dash-live {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.14);
}
.dash-live i {
  font-size: 0.45rem;
}
.dash-live.is-on i {
  color: #7dffcf;
  animation: dash-pulse 1.6s ease-in-out infinite;
}
.dash-live.is-off i {
  color: #ffb4b4;
}
.dash-empty {
  text-align: center;
  color: var(--wc-text-muted);
  padding: 2.25rem 1rem;
}
.dash-empty i {
  font-size: 1.75rem;
  display: block;
  margin-bottom: 0.75rem;
  opacity: 0.55;
}
.dash-empty p {
  margin-bottom: 0.75rem;
}
.dash-ws-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}
.dash-ws-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-lg);
  background: var(--wc-bg-subtle);
  color: inherit;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.dash-ws-card:hover {
  transform: translateY(-2px);
  border-color: var(--wc-primary);
  box-shadow: var(--wc-shadow-sm);
  color: inherit;
  text-decoration: none;
}
.dash-ws-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wc-primary-light);
  color: var(--wc-primary);
  flex-shrink: 0;
}
.dash-ws-card__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.dash-ws-card__body strong {
  font-size: 0.9375rem;
  color: var(--wc-text-heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dash-ws-card__body small {
  color: var(--wc-text-muted);
  font-size: 0.75rem;
}
.dash-ws-card__go {
  color: var(--wc-text-muted);
  font-size: 0.75rem;
}
.dash-health {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.dash-health__track {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: var(--wc-border);
  overflow: hidden;
}
.dash-health__fill {
  height: 100%;
  background: var(--wc-success);
  border-radius: inherit;
  transition: width 0.3s ease;
}
.dash-health__label {
  font-size: 0.75rem;
  color: var(--wc-text-muted);
  white-space: nowrap;
}
.dash-device-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.dash-device {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--wc-border);
}
.dash-device:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}
.dash-device__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dash-device__dot.is-on {
  background: var(--wc-success);
  box-shadow: 0 0 0 4px rgba(34, 198, 171, 0.16);
}
.dash-device__dot.is-off {
  background: #c5cbd3;
}
.dash-device__main {
  flex: 1;
  min-width: 0;
}
.dash-device__main strong {
  display: block;
  font-size: 0.875rem;
  color: var(--wc-text-heading);
}
.dash-device__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.2rem;
}
.dash-device__meta span {
  font-size: 0.7rem;
  color: var(--wc-text-muted);
  background: var(--wc-bg-subtle);
  border: 1px solid var(--wc-border);
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
}
.dash-actions {
  display: grid;
  gap: 0.625rem;
}
.dash-action {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem 0.95rem;
  border: 1px solid var(--wc-border);
  border-radius: var(--wc-radius-md);
  color: var(--wc-text-heading);
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.dash-action i {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--wc-primary-light);
  color: var(--wc-primary);
}
.dash-action:hover {
  border-color: var(--wc-primary);
  background: var(--wc-primary-light);
  color: var(--wc-text-heading);
  text-decoration: none;
}
.dash-feed {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 320px;
  overflow: auto;
}
.dash-feed__item {
  display: grid;
  grid-template-columns: 4.75rem 1fr;
  gap: 0.5rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid var(--wc-border);
  font-size: 0.8125rem;
}
.dash-feed__item:last-child {
  border-bottom: 0;
}
.dash-feed__time {
  color: var(--wc-text-muted);
  font-variant-numeric: tabular-nums;
  font-size: 0.75rem;
}
.dash-feed__body {
  color: var(--wc-text);
  min-width: 0;
}
.dash-feed__body strong {
  display: block;
  color: var(--wc-text-heading);
  font-size: 0.8125rem;
}
.dash-feed__body em {
  font-style: normal;
  color: var(--wc-text-muted);
  margin-left: 0.25rem;
}
@keyframes dash-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@media (max-width: 767.98px) {
  .dash-hero {
    padding: 1.25rem;
  }
  .dash-hero__title {
    font-size: 1.3rem;
  }
  .dash-hero__aside {
    text-align: left;
  }
}
</style>
